import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Save, Globe } from "lucide-react";
import { applyTheme, GYM_THEMES, resolveThemeId } from "@/lib/gymThemes";

export default function AdminSettings() {
  const [s, setS] = useState(null);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data || {}));
  }, []);

  if (!s) return <div className="text-white/40">Cargando…</div>;

  const save = async () => {
    const body = { ...s };
    delete body.id;
    delete body._id;
    delete body.theme_custom;
    const { data } = await api.put("/admin/settings", body);
    applyTheme(body.theme || "crimson-knockout");
    window.dispatchEvent(new CustomEvent("app-settings-updated", { detail: data || body }));
    toast.success("Configuración guardada");
  };

  const F = (label, key, type = "text") => (
    <div>
      <label className="text-xs uppercase tracking-widest text-white/60">{label}</label>
      <input type={type} className="input-dark mt-1" value={s[key] || ""} onChange={(e) => setS({ ...s, [key]: e.target.value })} />
    </div>
  );

  return (
    <div data-testid="admin-settings-page">
      <div className="text-xs uppercase tracking-[0.3em] text-[#E63946]">Sistema</div>
      <h1 className="font-display text-4xl text-white mt-1">Configuración</h1>

      <div className="mt-6 glass p-6 rounded-sm max-w-2xl border border-[#E63946]/20">
        <h2 className="font-display text-xl text-white flex items-center gap-2">
          <Globe size={18} className="text-[#E63946]" /> Sitio público
        </h2>
        <p className="text-sm text-white/55 mt-2">
          Nombre, logo, textos del home, carrusel, redes sociales y encabezados de páginas se editan en Sitio público.
        </p>
        <Link to="/admin/sitio-publico" className="btn-outline text-sm py-2 mt-4 inline-flex">
          Ir a Sitio público
        </Link>
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div className="glass p-6 rounded-sm space-y-4">
          <h2 className="font-display text-2xl text-white">Operación</h2>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/60">Horario de atención</label>
            <textarea
              rows={4}
              className="input-dark mt-1 resize-none w-full"
              value={s.gym_hours || ""}
              onChange={(e) => setS({ ...s, gym_hours: e.target.value })}
              placeholder={"Lunes a Viernes: 6:00 – 22:00\nSábado: 7:00 – 14:00"}
            />
            <p className="text-xs text-white/45 mt-1">Se muestra en la página de paquetes.</p>
          </div>
          {F("Email de contacto (notificaciones internas)", "contact_email", "email")}
        </div>

        <div className="glass p-6 rounded-sm space-y-4">
          <h2 className="font-display text-2xl text-white">Localización</h2>
          <div className="grid grid-cols-2 gap-3">
            {F("Moneda (código)", "currency")}
            {F("Símbolo", "currency_symbol")}
          </div>
          {F("Zona horaria", "timezone")}
        </div>

        <div className="glass p-6 rounded-sm space-y-4 lg:col-span-2">
          <h2 className="font-display text-2xl text-white">Asistencia (kiosk)</h2>
          {s.attendance_licensed === false ? (
            <p className="text-sm text-amber-400/90 border border-amber-500/30 rounded-sm p-4">
              El módulo de asistencia no está licenciado. Contacta al super administrador para activarlo.
            </p>
          ) : (
          <>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="w-5 h-5 accent-green-600"
              checked={s.attendance_enabled !== false}
              onChange={(e) => setS({ ...s, attendance_enabled: e.target.checked })}
            />
            <span className="text-white">Módulo de asistencia activo</span>
          </label>
          <p className="text-xs text-white/40">
            Cada atleta puede registrar una sola asistencia válida por día calendario (horario del gimnasio).
          </p>
          </>
          )}
        </div>

        <div className="glass p-6 rounded-sm space-y-4">
          <h2 className="font-display text-2xl text-white">Tema visual</h2>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/60">Tema de colores</label>
            <select
              className="input-dark mt-1"
              value={resolveThemeId(s.theme)}
              onChange={(e) => {
                const next = e.target.value;
                setS({ ...s, theme: next });
                applyTheme(next);
              }}
            >
              {GYM_THEMES.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
            <p className="text-xs text-white/45 mt-2">
              {GYM_THEMES.find((t) => t.id === resolveThemeId(s.theme))?.hint || GYM_THEMES[0].hint}
            </p>
          </div>
        </div>

        <div className="glass p-6 rounded-sm space-y-4">
          <h2 className="font-display text-2xl text-white">Pagos con tarjeta (Mercado Pago)</h2>
          {!s.online_payments_licensed ? (
            <p className="text-sm text-amber-400/90 border border-amber-500/30 rounded-sm p-4">
              El módulo de pagos en línea no está licenciado. Contacta al super administrador.
            </p>
          ) : (
          <>
          <label className="flex items-center gap-2 text-sm text-white/80">
            <input
              type="checkbox"
              checked={!!s.mp_enabled}
              onChange={(e) => setS({ ...s, mp_enabled: e.target.checked })}
            />
            Habilitar pagos en línea
          </label>
          {F("Public key", "mp_public_key")}
          {F("Access token", "mp_access_token")}
          {F("Webhook secret (opcional)", "mp_webhook_secret")}
          </>
          )}
        </div>
      </div>

      <button onClick={save} className="btn-primary mt-8" data-testid="save-settings">
        <Save size={14} /> Guardar configuración
      </button>
    </div>
  );
}
